<script setup lang="ts">
import { computed } from "vue";

import TextareaInputAlt from "@/dsComponents/inputs/TextareaInputAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

interface TextEditorModalProps {
    open: boolean;
    title: string;
    heading?: string;
    placeholder?: string;
    disabled?: boolean;
    disableOverlayClose?: boolean;
    height?: string;
}

const props = withDefaults(defineProps<TextEditorModalProps>(), {
    heading: undefined,
    placeholder: undefined,
    disabled: false,
    disableOverlayClose: false,
    height: "auto",
});

const text = defineModel<string>({ required: true });

const emit = defineEmits<{
    close: [];
}>();

const hasFixedHeight = computed(() => props.height !== "auto");
</script>

<template>
    <Modal
        :open="open"
        width="960px"
        max-height="90vh"
        :height="height"
        position="top"
        no-padding
        content-overflow="hidden"
        :z-index-level="1"
        :disable-overlay-close="disableOverlayClose"
        @close="emit('close')"
    >
        <div class="text-editor-modal" :class="{ 'text-editor-modal--fixed-height': hasFixedHeight }">
            <div class="text-editor-modal__top">
                <span class="text-editor-modal__top-title typo-sm-medium">
                    {{ title }}
                </span>
            </div>
            <div class="text-editor-modal__body">
                <div class="text-editor-modal__content-area">
                    <h1 v-if="heading" class="text-editor-modal__heading">
                        {{ heading }}
                    </h1>
                    <TextareaInputAlt
                        v-model="text"
                        class="text-editor-modal__text"
                        :placeholder="placeholder"
                        :disabled="disabled"
                        :rows="1"
                        :auto-resize="!hasFixedHeight"
                        :fill-parent-height="hasFixedHeight"
                        no-border
                        font-size="16px"
                        textarea-font-weight="500"
                    />
                </div>
            </div>
            <div class="text-editor-modal__footer">
                <div class="text-editor-modal__footer-start">
                    <slot name="footer-start" />
                </div>
                <div class="text-editor-modal__footer-end">
                    <slot name="footer-end" />
                </div>
            </div>
        </div>
    </Modal>
</template>

<style scoped lang="scss">
.text-editor-modal {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    max-height: 90vh;
    background-color: var(--bg-white-0, #ffffff);

    &__top {
        display: flex;
        align-items: center;
        height: 48px;
        padding: 0 var(--spacing-3xl, 24px);
        gap: var(--spacing-xl, 16px);
        flex-shrink: 0;
        border-bottom: 1px solid var(--stroke-soft-200, #dbdfe6);
        background-color: var(--bg-white-0, #ffffff);
    }

    &__top-title {
        color: var(--text-main-900, #00061a);
    }

    &__body {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        background-color: var(--bg-white-0, #ffffff);
        padding: var(--spacing-5xl, 40px) var(--spacing-3xl, 24px) var(--spacing-3xl, 24px);
    }

    &__content-area {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3xl, 24px);
        max-width: 800px;
        margin: 0 auto;
        width: 100%;
    }

    &__heading {
        margin: 0;
        font-size: 32px;
        font-weight: 500;
        line-height: 40px;
        color: var(--text-main-900, #00061a);
    }

    &__footer {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-lg, 12px);
        padding: var(--spacing-xl, 16px) var(--spacing-3xl, 24px);
        border-top: 1px solid var(--stroke-soft-200, #dbdfe6);
        background-color: var(--bg-white-0, #ffffff);
    }

    &--fixed-height &__body {
        display: flex;
        flex-direction: column;
    }

    &--fixed-height &__content-area {
        flex: 1;
        min-height: 0;
    }

    &__footer-start,
    &__footer-end {
        display: flex;
        align-items: center;
        gap: var(--spacing-lg, 12px);
    }
}
</style>

<style lang="scss">
.modal-card:has(.text-editor-modal) {
    overflow: hidden;
    max-height: 90vh;
}
</style>
